// Cloud simulation: spawn, update, wrap-around
// Two layers above the world with height-based depth separation.
// Higher height = appears more elevated above ground (shadow offset).

const LAYER_CFG = [
  { name: 'far',  height: 100, count: 8,  minScale: 0.9, maxScale: 1.4, speed: 8,  alpha: 0.18 },
  { name: 'near', height: 60,  count: 14, minScale: 0.7, maxScale: 1.1, speed: 14, alpha: 0.22 },
];

let nextId = 0;

function seededRand(seed) {
  let s = seed | 0;
  return () => {
    s = (s * 1664525 + 1013904223) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function makeCircles(rng, count) {
  const circles = [];
  const n = count || (6 + Math.floor(rng() * 5)); // 6-10
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2 + (rng() - 0.5) * 0.4;
    const rx = 40 + rng() * 30; // ellipse x radius
    const ry = 18 + rng() * 12; // ellipse y radius
    circles.push({
      ox: Math.cos(angle) * rx,
      oy: Math.sin(angle) * ry,
      r: 18 + rng() * 22,
    });
  }
  // center blob
  circles.push({ ox: 0, oy: 0, r: 28 + rng() * 16 });
  return circles;
}

function cloudBounds(circles, scale) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of circles) {
    const cx = c.ox * scale, cy = c.oy * scale, r = c.r * scale;
    if (cx - r < minX) minX = cx - r;
    if (cy - r < minY) minY = cy - r;
    if (cx + r > maxX) maxX = cx + r;
    if (cy + r > maxY) maxY = cy + r;
  }
  return { minX, minY, maxX, maxY, w: maxX - minX, h: maxY - minY };
}

function spawnCloud(layerCfg, wrapRect) {
  const id = nextId++;
  const rng = seededRand(id * 7919 + 31);
  const baseScale = layerCfg.minScale + rng() * (layerCfg.maxScale - layerCfg.minScale);
  const circles = makeCircles(rng);
  const bounds = cloudBounds(circles, baseScale);

  return {
    id,
    // world position
    wx: wrapRect.x + rng() * wrapRect.w,
    wy: wrapRect.y + rng() * wrapRect.h,
    vx: layerCfg.speed * (0.6 + rng() * 0.8),
    vy: layerCfg.speed * (rng() - 0.5) * 0.15,
    baseScale,
    alpha: layerCfg.alpha * (0.7 + rng() * 0.3),
    circles,
    bounds,
    wobblePhase: rng() * Math.PI * 2,
    wobbleSpeed: 0.3 + rng() * 0.4,
    wobbleAmp: 2 + rng() * 3,
  };
}

export class CloudSystem {
  constructor() {
    this.layers = LAYER_CFG.map(cfg => ({
      cfg,
      clouds: [],
    }));
    this._inited = false;
    /** @type {import('../../world/weather/WeatherState.mjs').WeatherState|null} */
    this.weatherState = null;
  }

  /** Call once per frame */
  update(dt, camera, canvasW, canvasH) {
    // dt in seconds
    const dtSec = Math.min(dt / 1000, 0.1); // clamp to avoid huge jumps

    // Weather-driven cloud count multiplier: 0.5x (clear) to 2.0x (full cover)
    const ws = this.weatherState;
    const coverMul = ws ? 0.5 + ws.cloudCover01 * 1.5 : 1;
    // Wind speed boost from weather
    const windBoost = ws ? 1 + ws.wind.x * 0.02 : 1;

    for (const layer of this.layers) {
      const { cfg, clouds } = layer;
      const effectiveCount = Math.round(cfg.count * coverMul);

      // Compute wrap rect: viewport in world coords, expanded by margin
      const margin = 300;
      const halfW = (canvasW / 2) / camera.zoom;
      const halfH = (canvasH / 2) / camera.zoom;
      const wrapRect = {
        x: camera.x - halfW - margin,
        y: camera.y - halfH - margin,
        w: (halfW + margin) * 2,
        h: (halfH + margin) * 2,
      };

      // Initial spawn
      if (!this._inited) {
        for (let i = clouds.length; i < effectiveCount; i++) {
          clouds.push(spawnCloud(cfg, wrapRect));
        }
      }

      // Move clouds (with wind boost)
      for (const c of clouds) {
        c.wx += c.vx * dtSec * windBoost;
        c.wy += c.vy * dtSec;
      }

      // Wrap-around: if cloud leaves the wrap rect on the right, respawn on the left
      for (let i = 0; i < clouds.length; i++) {
        const c = clouds[i];
        const hw = (c.bounds.w / 2) + 50;
        if (c.wx - hw > wrapRect.x + wrapRect.w) {
          // Respawn on left edge
          c.wx = wrapRect.x - hw;
          c.wy = wrapRect.y + Math.random() * wrapRect.h;
        }
      }

      // Shrink cloud array when effectiveCount drops below current count
      while (clouds.length > effectiveCount) {
        clouds.pop();
      }

      // Maintain count — grow when effectiveCount rises
      while (clouds.length < effectiveCount) {
        const nc = spawnCloud(cfg, wrapRect);
        nc.wx = wrapRect.x - (nc.bounds.w / 2) - 50; // enter from left
        clouds.push(nc);
      }
    }
    this._inited = true;
  }

  /** Get clouds visible on screen for a given layer index */
  getVisibleClouds(layerIdx, camera, canvasW, canvasH) {
    const layer = this.layers[layerIdx];
    if (!layer) return [];
    const { cfg, clouds } = layer;
    const visible = [];

    for (const c of clouds) {
      // Screen position: 1:1 with tiles, elevated by height
      const sx = (c.wx - camera.x) * camera.zoom + canvasW / 2;
      const sy = (c.wy - camera.y) * camera.zoom + canvasH / 2 - cfg.height * camera.zoom;
      const scale = c.baseScale * camera.zoom;

      // AABB culling
      const hw = (c.bounds.w / 2) * scale + 50;
      const hh = (c.bounds.h / 2) * scale + 50;
      if (sx + hw < 0 || sx - hw > canvasW) continue;
      if (sy + hh < 0 || sy - hh > canvasH) continue;

      visible.push({ cloud: c, sx, sy, scale });
    }
    return visible;
  }

  /**
   * Get clouds with screen positions projected onto the ground plane (no height offset).
   * Used by shadow renderer and precipitation so positions align with tiles.
   */
  getGroundProjectedClouds(layerIdx, camera, canvasW, canvasH) {
    const layer = this.layers[layerIdx];
    if (!layer) return [];
    const { cfg, clouds } = layer;
    const visible = [];

    for (const c of clouds) {
      // Project at ground level (no height offset)
      const sx = (c.wx - camera.x) * camera.zoom + canvasW / 2;
      const sy = (c.wy - camera.y) * camera.zoom + canvasH / 2;
      const scale = c.baseScale * camera.zoom;

      const hw = (c.bounds.w / 2) * scale + 80;
      const hh = (c.bounds.h / 2) * scale + 80;
      if (sx + hw < 0 || sx - hw > canvasW) continue;
      if (sy + hh < 0 || sy - hh > canvasH) continue;

      visible.push({ cloud: c, sx, sy, scale, height: cfg.height });
    }
    return visible;
  }

  get layerCount() {
    return this.layers.length;
  }

  getHeight(layerIdx) {
    return this.layers[layerIdx]?.cfg.height ?? 0;
  }
}
